import type { ReactElement } from 'react';
import { useEffect, useLayoutEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { useKeyPress, useMediaQuery } from '../../../ui-hooks';

import type { TModalProps } from './modal.interface';
import {
  containerVariants,
  createCommonStyle,
  DEFAULT_ANIMATION,
  getSizeModal
} from '.';

export const Modal = ({
  children,
  open = false,
  opacity = 5,
  size = 'md',
  ariaDescribedby = '',
  ariaLabelledby = '',
  onClose,
  disableEscapeKeyDown = false,
  animation,
  fullWidth = false,
  disableBodyScroll = false,
  rounded = false,
  className = '',
  breakpoint = 'xl'
}: TModalProps): ReactElement => {
  const isMobile = useMediaQuery(breakpoint || 'lg');
  const isPressEscape = useKeyPress('Escape');

  useLayoutEffect(() => {
    if (disableBodyScroll) {
      const html = document.documentElement.style;
      open ? (html.overflow = 'hidden') : (html.overflow = 'auto');
    }
  }, [open, disableBodyScroll]);

  useEffect(() => {
    if (!disableEscapeKeyDown && isPressEscape && open) {
      onClose && onClose();
    }
  }, [isPressEscape, open, onClose, disableEscapeKeyDown]);

  return (
    <AnimatePresence mode="wait">
      {open && (
        <motion.div
          className={`
                    co-bg-black-alpha-${opacity}
                    co-fixed
                    co-w-full
                    co-h-full
                    co-right-0
                    co-left-0
                    co-top-0
                    co-bottom-0
                    co-z-100`}
          variants={containerVariants}
          initial="exit"
          animate="enter"
          exit="exit"
        >
          <div className="co-h-full co-w-full co-flex">
            {isMobile ? (
              <motion.div
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                className={`
                    ${createCommonStyle(isMobile, rounded)}
                    ${fullWidth ? 'co-w-full' : getSizeModal(size)}
                    co-h-full
                    ${className}
                `}
                aria-labelledby={ariaLabelledby}
                aria-describedby={ariaDescribedby}
                {...DEFAULT_ANIMATION}
              >
                <div
                  className={`
                                    ${
                                      animation?.to
                                        ? `co-h-${
                                            100 - Number(animation.to)
                                          }v test`
                                        : 'co-h-full '
                                    }

                                    co-flex
                                    `}
                >
                  {children}
                </div>
              </motion.div>
            ) : (
              <motion.div
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby={ariaLabelledby}
                aria-describedby={ariaDescribedby}
                className={`
                                ${getSizeModal(size)}
                                ${createCommonStyle(isMobile, rounded)}
                                co-h-auto
                                co-w-full
                                ${className}`}
                {...DEFAULT_ANIMATION}
              >
                {children}
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
